import React from "react";
import { Box, Typography, Paper, Divider, Button, Grow } from "@mui/material";
import Popper from "@mui/material/Popper";
import { createPortal } from "react-dom";
import PassengerCounter from "./PassengerCounter";
import {
  PASSENGER_TYPES,
  CABIN_CLASSES,
} from "../constants/flightSearchConstants";

const PassengerMenu = ({
  anchorEl,
  open,
  onClose,
  // Passenger state
  adults,
  setAdults,
  students,
  setStudents,
  seniors,
  setSeniors,
  youths,
  setYouths,
  childrenCount,
  setChildrenCount,
  toddlers,
  setToddlers,
  infants,
  setInfants,
  // Cabin state
  cabin,
  setCabin,
  // Flight segments for updating cabin
  flightSegments,
  setFlightSegments,
  // Context prop to detect if on result page
  isResultPage = false,
  isExpanded = false,
}) => {
  // Add click outside handler
  React.useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      // Check if click is outside the passenger menu
      const passengerMenuElement = document.querySelector('[data-passenger-menu]');
      if (passengerMenuElement && !passengerMenuElement.contains(event.target)) {
        // Also check if click is outside the anchor element
        if (anchorEl && !anchorEl.contains(event.target)) {
          onClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, anchorEl, onClose]);

  const passengerStates = {
    adults: { value: adults, setter: setAdults },
    students: { value: students, setter: setStudents },
    seniors: { value: seniors, setter: setSeniors },
    youths: { value: youths, setter: setYouths },
    children: { value: childrenCount, setter: setChildrenCount },
    toddlers: { value: toddlers, setter: setToddlers },
    infants: { value: infants, setter: setInfants },
  };

  if (!open || !anchorEl) return null;

  const menuHeight = 600;

  const passengerMenuContent = (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-end"
      modifiers={[
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'preventOverflow', options: { padding: 8 } },
        { name: 'flip', options: { fallbackPlacements: ['top-end', 'bottom-start'] } },
      ]}
      transition
      style={{ zIndex: 1300 }}
    >
      {({ TransitionProps }) => (
        <Grow {...TransitionProps} style={{ transformOrigin: 'right top' }}>
          <Paper
            data-passenger-menu
            onClick={(e) => e.stopPropagation()}
            sx={{
              borderRadius: 2,
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
              p: 0,
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              width: "400px",
              maxWidth: '400px',
              maxHeight: `${menuHeight}px`,
              overflowY: 'auto',
              mt: 0,
            }}
          >
      <Box sx={{ p: 2.5 }}>
        {/* Travelers Section */}
        <Typography
          variant="h6"
          sx={{ 
            fontWeight: 600, 
            fontSize: "16px", 
            mb: 2.5, 
            color: "#333",
            fontFamily: "inherit"
          }}
        >
          Travelers
        </Typography>

        {PASSENGER_TYPES.map((passengerType) => (
          <PassengerCounter
            key={passengerType.key}
            label={passengerType.label}
            subtitle={passengerType.subtitle}
            value={passengerStates[passengerType.key].value}
            setValue={passengerStates[passengerType.key].setter}
            min={passengerType.min}
            max={passengerType.max}
          />
        ))}

        <Divider sx={{ my: 2.5 }} />

        {/* Cabin Class Section */}
        <Typography
          variant="h6"
          sx={{ 
            fontWeight: 600, 
            fontSize: "16px", 
            mb: 2.5, 
            color: "#333",
            fontFamily: "inherit"
          }}
        >
          Cabin class
        </Typography>

        <Box sx={{ display: "flex", gap: 1.5 , flexWrap: 'wrap'}}>
          {CABIN_CLASSES.map((classType) => (
            <Button
              key={classType}
              variant={cabin === classType ? "contained" : "outlined"}
              onClick={() => {
                setCabin(classType);
                // Update cabin for all segments
                setFlightSegments(
                  flightSegments.map((segment) => ({
                    ...segment,
                    cabin: classType,
                  }))
                );
              }}
              sx={{
                cursor: "pointer",
                backgroundColor: cabin === classType ? "#f0f0f0" : "transparent",
                color: "#333",
                border: cabin === classType ? "1px solid #333" : "1px solid #ccc",
                borderRadius: 1.5,
                px: 2,
                py: 1,
                fontSize: "13px",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "100px",
                height: "32px",
                "&:hover": {
                  backgroundColor: cabin === classType ? "#e0e0e0" : "#f5f5f5",
                  border: cabin === classType ? "1px solid #333" : "1px solid #ccc",
                },
                "&.MuiButton-contained": {
                  backgroundColor: "#f0f0f0",
                  color: "#333",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                    boxShadow: "none"
                  }
                }
              }}
            >
              {classType}
            </Button>
          ))}
        </Box>
      </Box>
          </Paper>
        </Grow>
      )}
    </Popper>
  );

  return createPortal(passengerMenuContent, document.body);
};

export default PassengerMenu;
